import React from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import type { MomentData } from '../../../services/momentsApi';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';
import type { NormalizedPhase } from '../containers/SlideoutCustomAccordionSection';
import { showOptimisticUpdateErrorToast } from '../utils/showOptimisticUpdateErrorToast';

export type SlideoutStatusHandlersContext = {
  card: SlideOutPanelCard | null | undefined;
  apiData: MomentData | null;
  setApiData: React.Dispatch<React.SetStateAction<MomentData | null>>;
  normalizedPhases: NormalizedPhase[];
  seamlessRefreshAndNotifications: (response?: any) => void | Promise<void>;
  setIsOverviewExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFeaturesExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDecisionsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDACIExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAdditionalInfoExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsGeneralInfoExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomExpandedPhases: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedInfoSection: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export type SlideoutStatusHandlers = {
  handleNonPhasedStatusAction: (action: React.Key) => Promise<void>;
  handleStatusAction: (action: React.Key, phaseId: string) => Promise<void>;
  handleSecondaryStatusAction: (
    action: React.Key,
    phaseId: string
  ) => Promise<void>;
};

/**
 * Extracted verbatim from SlideOutPanel/index.tsx: the three status-action handlers for the
 * status dropdown (non-phased, phased, and the secondary-date menu rows).
 */
export function useSlideoutStatusHandlers(
  ctx: SlideoutStatusHandlersContext
): SlideoutStatusHandlers {
  const {
    card,
    apiData,
    setApiData,
    normalizedPhases,
    seamlessRefreshAndNotifications,
    setIsOverviewExpanded,
    setIsFeaturesExpanded,
    setIsDecisionsExpanded,
    setIsDACIExpanded,
    setIsAdditionalInfoExpanded,
    setIsGeneralInfoExpanded,
    setCustomExpandedPhases,
    setExpandedInfoSection
  } = ctx;

  // Handler for non-phased status dropdown actions
  const handleNonPhasedStatusAction = async (action: React.Key) => {
    if (!card?.issueId) {
      return;
    }

    const completionDate = new Date().toISOString().split('T')[0];

    switch (action) {
      case 'markComplete': {
        // Capture old status before update (for optimistic UI / rollback)
        const oldStatus = apiData?.status || 'ACTIVE';

        // Optimistic update for non-phased
        setApiData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            status: 'COMPLETED',
            statusUpdatedAt: completionDate
          } as MomentData;
        });

        // Use non-phased status update utility (includes statusUpdatedAt timestamp)
        try {
          const { updateNonPhasedStatusField } = await import(
            '../utils/fieldUpdateUtils'
          );
          await updateNonPhasedStatusField(
            card.issueId,
            'COMPLETED',
            response => {
              seamlessRefreshAndNotifications(response);
            },
            error => {
              showOptimisticUpdateErrorToast(
                'Failed to update status. Please try again.',
                error
              );
              // Revert optimistic update on error
              setApiData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  status: 'ACTIVE', // Revert to active
                  statusUpdatedAt: undefined
                } as MomentData;
              });
            },
            completionDate // Pass the completion date for statusUpdatedAt
          );
        } catch (error) {}
        break;
      }
      case 'activate': {
        // Capture old status before update (for optimistic UI / rollback)
        const oldStatus = apiData?.status || 'COMPLETED';

        // Optimistic update for non-phased
        setApiData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            status: 'ACTIVE',
            statusUpdatedAt: undefined
          } as MomentData;
        });

        // Expand all nested accordions when activating
        setIsGeneralInfoExpanded(true);
        // Persist to localStorage
        localStorage.setItem('slideout_generalInfo_expanded', 'true');
        setIsOverviewExpanded(true);
        localStorage.setItem('slideout_overview_expanded', 'true');
        setIsFeaturesExpanded(true);
        // Features persistence is handled by FeaturesContainer
        localStorage.setItem('slideout_features_expanded', 'true');
        setIsDecisionsExpanded(true);
        setIsDACIExpanded(true);
        setIsAdditionalInfoExpanded(true);
        // Expand the info section (General Info + Primary Contacts)
        setExpandedInfoSection(new Set([card.issueId]));

        // Use non-phased field update utility
        try {
          const { updateNonPhasedField } = await import(
            '../utils/fieldUpdateUtils'
          );
          await updateNonPhasedField(
            card.issueId,
            'status',
            'ACTIVE',
            response => {
              seamlessRefreshAndNotifications(response);
            },
            error => {
              showOptimisticUpdateErrorToast(
                'Failed to update status. Please try again.',
                error
              );
              // Revert optimistic update on error
              setApiData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  generalInfo: {
                    ...prev.generalInfo,
                    status: 'COMPLETED' // Revert to completed
                  }
                } as MomentData;
              });
            }
          );
        } catch (error) {
          showOptimisticUpdateErrorToast(
            'Failed to update status. Please try again.',
            error
          );
        }
        break;
      }
    }
  };

  // Handler for status dropdown actions (phase-specific and non-phased)
  const handleStatusAction = async (action: React.Key, phaseId: string) => {
    if (!card?.issueId) {
      return;
    }

    // Check if this is a non-phased moment (momentId = issueId or 'moment-section')
    const isNonPhased = phaseId === (card?.issueId || 'moment-section');

    if (isNonPhased) {
      return handleNonPhasedStatusAction(action);
    }

    // Find the phase to get its name for the API payload (only for phased)
    const targetPhase = normalizedPhases.find(p => p.id === phaseId);
    const phaseName = targetPhase?.phaseName;

    switch (action) {
      case 'markComplete': {
        const completionDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const oldPhaseStatus = targetPhase?.status || 'ACTIVE';

        // Optimistic update
        setApiData(prev => {
          if (!prev) return prev;
          const next: any = { ...prev };
          next.releasePhases = (next.releasePhases || []).map((p: any) =>
            p.id === phaseId
              ? {
                  ...p,
                  status: 'COMPLETED',
                  statusUpdatedAt: completionDate,
                  generalInfo: p.generalInfo
                }
              : p
          );
          return next;
        });

        // Use phased field update utility for proper API payload structure
        const { updatePhasedStatusField } = await import(
          '../utils/phasedFieldUpdateUtils'
        );
        await updatePhasedStatusField(
          card.issueId,
          phaseId,
          'COMPLETED',
          response => {
            seamlessRefreshAndNotifications(response);
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update status. Please try again.',
              error
            );
            // Revert optimistic update on error
            setApiData(prev => {
              if (!prev) return prev;
              const next: any = { ...prev };
              next.releasePhases = (next.releasePhases || []).map((p: any) =>
                p.id === phaseId
                  ? {
                      ...p,
                      status: p.status, // Keep original status
                      statusUpdatedAt: undefined,
                      generalInfo: p.generalInfo
                    }
                  : p
              );
              return next;
            });
          },
          phaseName,
          completionDate
        );
        break;
      }
      case 'activate': {
        const oldPhaseStatusActivate = targetPhase?.status || 'COMPLETED';
        setApiData(prev => {
          if (!prev) return prev;
          const next: any = { ...prev };
          next.releasePhases = (next.releasePhases || []).map((p: any) =>
            p.id === phaseId
              ? {
                  ...p,
                  status: 'ACTIVE',
                  generalInfo: p.generalInfo
                }
              : p
          );
          return next;
        });

        // Expand all nested accordions when activating
        setIsOverviewExpanded(true);
        localStorage.setItem('slideout_overview_expanded', 'true');
        setIsFeaturesExpanded(true);
        localStorage.setItem('slideout_features_expanded', 'true');
        setIsDecisionsExpanded(true);
        setIsDACIExpanded(true);
        setIsAdditionalInfoExpanded(true);
        // Expand the phase accordion and info section
        setCustomExpandedPhases(prev => {
          const newSet = new Set(prev);
          newSet.add(phaseId);
          return newSet;
        });
        setExpandedInfoSection(prev => {
          const newSet = new Set(prev);
          newSet.add(phaseId);
          return newSet;
        });
        // Persist expanded state to localStorage (global setting)
        localStorage.setItem('slideout_generalInfo_expanded', 'true');

        // Use phased field update utility for proper API payload structure
        const { updatePhasedStatusField } = await import(
          '../utils/phasedFieldUpdateUtils'
        );
        await updatePhasedStatusField(
          card.issueId,
          phaseId,
          'ACTIVE',
          response => {
            seamlessRefreshAndNotifications(response);
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update status. Please try again.',
              error
            );
            // Revert optimistic update on error
            setApiData(prev => {
              if (!prev) return prev;
              const next: any = { ...prev };
              next.releasePhases = (next.releasePhases || []).map((p: any) =>
                p.id === phaseId
                  ? {
                      ...p,
                      status: p.status, // Keep original status
                      generalInfo: p.generalInfo
                    }
                  : p
              );
              return next;
            });
          },
          phaseName
        );
        break;
      }
      case 'removePhase': {
        const oldPhaseStatusRemove = targetPhase?.status || 'ACTIVE';

        // Enforce at least one active phase - count all non-N/A phases before removing
        const activePhases = normalizedPhases.filter(
          p => p.status !== 'N/A' && p.status !== 'n/a'
        );
        const activePhaseCount = activePhases.length;
        const targetPhaseStatus = targetPhase?.status;
        const isRemovingActivePhase =
          targetPhaseStatus !== 'N/A' && targetPhaseStatus !== 'n/a';

        // Prevent removal if this is the last active phase
        if (isRemovingActivePhase && activePhaseCount === 1) {
          ToastQueue.info(
            'At least one phase must be active for a release-type RSD.',
            { timeout: 3000, id: 'info-toast-remove-phase' }
          );
          return;
        }

        // NEW: Prevent removal when only Internal Beta and one other phase exist
        if (isRemovingActivePhase && activePhaseCount === 2) {
          const activePhaseNames = activePhases.map(p => p.phaseName);
          const hasInternalBeta = activePhaseNames.includes('Internal Beta');
          const removingPhaseName = targetPhase?.phaseName;

          // If Internal Beta exists and user is trying to remove the other phase
          if (hasInternalBeta && removingPhaseName !== 'Internal Beta') {
            ToastQueue.info(
              'At least one additional phase must be present along with Internal Beta release',
              {
                timeout: 3000,
                id: 'info-toast-remove-phase-internal-beta'
              }
            );
            return;
          }
        }

        setApiData(prev => {
          if (!prev) return prev;
          const next: any = { ...prev };
          next.releasePhases = (next.releasePhases || []).map((p: any) =>
            p.id === phaseId
              ? {
                  ...p,
                  status: 'N/A',
                  generalInfo: p.generalInfo
                }
              : p
          );
          return next;
        });

        // Use phased field update utility for proper API payload structure
        const { updatePhasedStatusField } = await import(
          '../utils/phasedFieldUpdateUtils'
        );
        await updatePhasedStatusField(
          card.issueId,
          phaseId,
          'N/A',
          response => {
            seamlessRefreshAndNotifications(response);
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update status. Please try again.',
              error
            );
            // Revert optimistic update on error
            setApiData(prev => {
              if (!prev) return prev;
              const next: any = { ...prev };
              next.releasePhases = (next.releasePhases || []).map((p: any) =>
                p.id === phaseId
                  ? {
                      ...p,
                      status: p.status, // Keep original status
                      generalInfo: p.generalInfo
                    }
                  : p
              );
              return next;
            });
          },
          phaseName
        );
        break;
      }
      default:
        break;
    }
  };

  // Handler for the secondary date status (Active/Completed) rows of the status menu.
  // Independent of showOnRoadmap ("Show Additional Card"): patching this never touches the
  // flag, and the flag alone still decides whether the additional timeline card renders.
  const handleSecondaryStatusAction = async (
    action: React.Key,
    phaseId: string
  ) => {
    if (!card?.issueId) {
      return;
    }

    const newValue = action === 'markComplete' ? 'COMPLETED' : 'ACTIVE';
    const isNonPhased = phaseId === (card?.issueId || 'moment-section');
    const targetPhase = normalizedPhases.find(p => p.id === phaseId);
    const oldValue = isNonPhased
      ? (apiData as any)?.secondaryDateStatus
      : (targetPhase as any)?.secondaryDateStatus;

    // Optimistic update
    setApiData(prev => {
      if (!prev) return prev;
      if (isNonPhased) {
        return {
          ...(prev as any),
          secondaryDateStatus: newValue
        } as MomentData;
      }
      const next: any = { ...prev };
      next.releasePhases = (next.releasePhases || []).map((p: any) =>
        p.id === phaseId ? { ...p, secondaryDateStatus: newValue } : p
      );
      return next;
    });

    const revert = () => {
      setApiData(prev => {
        if (!prev) return prev;
        if (isNonPhased) {
          return {
            ...(prev as any),
            secondaryDateStatus: oldValue
          } as MomentData;
        }
        const next: any = { ...prev };
        next.releasePhases = (next.releasePhases || []).map((p: any) =>
          p.id === phaseId ? { ...p, secondaryDateStatus: oldValue } : p
        );
        return next;
      });
    };

    try {
      if (isNonPhased) {
        const { updateNonPhasedField } = await import(
          '../utils/fieldUpdateUtils'
        );
        await updateNonPhasedField(
          card.issueId,
          'secondaryDateStatus',
          newValue,
          response => {
            seamlessRefreshAndNotifications(response);
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update secondary date status. Please try again.',
              error
            );
            revert();
          }
        );
      } else {
        const { updatePhasedField } = await import(
          '../utils/phasedFieldUpdateUtils'
        );
        await updatePhasedField(
          card.issueId,
          phaseId,
          'secondaryDateStatus',
          newValue,
          response => {
            seamlessRefreshAndNotifications(response);
          },
          error => {
            showOptimisticUpdateErrorToast(
              'Failed to update secondary date status. Please try again.',
              error
            );
            revert();
          },
          undefined,
          targetPhase?.phaseName
        );
      }
    } catch (error) {
      showOptimisticUpdateErrorToast(
        'Failed to update secondary date status. Please try again.',
        error
      );
      revert();
    }
  };

  return {
    handleNonPhasedStatusAction,
    handleStatusAction,
    handleSecondaryStatusAction
  };
}
